import { HttpClient } from '@wix/http-client';
import {
  TextOnImageGetPhotoSceneParams,
  TEXT_ON_IMAGE_BRIGHTNESS,
  TextOnImageNoPointPhotoParams,
  TEXT_ON_IMAGE_FLOWS,
  TextOnImageResponse,
} from '../types';
import {
  AI_GATEWAY_APP_DEF_FOR_GET_PROMPTS,
  PromptHubAcceptedUrls,
} from '../../chatController/sgChat/constants';
import {
  FLUX_DEV_NO_POINT_IMAGE_GENERATION_PROMPT_ID,
  PHOTO_SCENES_PROMPT_IDS,
} from './consts';
import { applyOpacityAndGetFile, reportTextOnImageError } from './utils';
import { SG_FEATURE_NAME } from '../../consts';

const DARK_IMAGE_REFERENCE =
  'https://static.wixstatic.com/media/47a484_1688cdc8a3ef442bbda0ec97b0269d33~mv2.png';

const BRIGHT_IMAGE_REFERENCE =
  'https://static.wixstatic.com/media/47a484_350a9393635e482d9063ebef6fbb7116~mv2.png';

export const getNoPointOfInterestImage = async (
  httpClient: HttpClient,
  sectionName: string,
  params: TextOnImageGetPhotoSceneParams,
  imageUploadHandlers: unknown,
): Promise<TextOnImageResponse | undefined> => {
  const { brightness } = params;
  const controlImageUrl = getImageReference(brightness);
  const photoScene = await getTextOnImagePhotoSceneNoPointFlow(
    httpClient,
    params,
  );

  const url = await getTextOnImageNoPointPhotoPromptHub(httpClient, {
    no_point1: photoScene,
    brightness,
    seedImage: controlImageUrl,
  });

  if (!url) return;

  const imageUrl = await applyOpacityAndGetFile(url, 0.75);
  return { imageUrl, controlImageUrl, photoScene };
};

const getImageReference = (brightness: TEXT_ON_IMAGE_BRIGHTNESS) => {
  return brightness === TEXT_ON_IMAGE_BRIGHTNESS.BRIGHT
    ? BRIGHT_IMAGE_REFERENCE
    : DARK_IMAGE_REFERENCE;
};

const getTextOnImagePhotoSceneNoPointFlow = async (
  httpClient: HttpClient,
  params: TextOnImageGetPhotoSceneParams,
): Promise<string> => {
  try {
    const { data } = await httpClient.post(
      PromptHubAcceptedUrls.AI_GATEWAY_URL,
      {
        appDefId: AI_GATEWAY_APP_DEF_FOR_GET_PROMPTS,
        promptId:
          PHOTO_SCENES_PROMPT_IDS[TEXT_ON_IMAGE_FLOWS.NO_POINT_OF_INTEREST],
        params,
        userRequestInfo: {
          featureName: SG_FEATURE_NAME,
        },
      },
    );
    const { no_point1 } = JSON.parse(data.response.generatedTexts[0]);
    return no_point1;
  } catch (error: any) {
    const errorResponse = error.response?.data;
    reportTextOnImageError(
      `Failed to get text on image photo scene: ${errorResponse}`,
    );
    return 'An abstract perspective of postmodern building facades, where angular lines and textured surfaces blend into muted tones of slate gray and soft charcoal. Subtle lighting outlines the intricate geometry, creating a balanced and sophisticated composition that exudes quiet elegance';
  }
};

const getTextOnImageNoPointPhotoPromptHub = async (
  httpClient: HttpClient,
  params: TextOnImageNoPointPhotoParams,
): Promise<string | undefined> => {
  try {
    const { data } = await httpClient.post(
      PromptHubAcceptedUrls.AI_GATEWAY_GET_RUNWARE_AI_IMAGE,
      {
        appDefId: AI_GATEWAY_APP_DEF_FOR_GET_PROMPTS,
        promptIdToFetch: FLUX_DEV_NO_POINT_IMAGE_GENERATION_PROMPT_ID,
        params,
      },
    );

    return data?.response?.url;
  } catch (error: any) {
    const errorResponse = error.response?.data;
    reportTextOnImageError(
      `Failed to get no point of interest flow image: ${errorResponse}`,
    );
  }
};
